import { useQuery } from '@tanstack/react-query'
import type { OrderListFilterOptions } from '@/features/order/order-list-types.ts'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { mergeOptions } from '@/utils/select-options.ts'

type BusinessType = 'lease' | 'valueAdded'

export type OrderListFilterRow = {
  businessId: number | null
  businessName: string | null
  customerId: number
  customerName: string
  salespersonId: number | null
  salespersonName: string
  subjectId: number | null
  subjectName: string | null
  siteId?: number
  siteName?: string
}

export function useOrderListFilterOptions({
  businessType,
  rows = [],
  includeSite = false
}: {
  businessType: BusinessType
  rows?: readonly OrderListFilterRow[]
  includeSite?: boolean
}): OrderListFilterOptions {
  const trpc = useTRPC()
  const businessSearch = useRemoteKeyword()
  const customerSearch = useRemoteKeyword()
  const salespersonSearch = useRemoteKeyword()
  const subjectSearch = useRemoteKeyword()
  const siteSearch = useRemoteKeyword()

  const businesses = useQuery({
    ...trpc.lookup.businesses.queryOptions({
      purpose: businessType === 'lease' ? 'order.rental.read' : 'order.service.read',
      keyword: businessSearch.keyword,
      type: businessType
    }),
    staleTime: 60_000
  })
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({
      purpose: businessType === 'lease' ? 'order.rental.read' : 'order.service.read',
      keyword: customerSearch.keyword
    }),
    staleTime: 60_000
  })
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({
      purpose: businessType === 'lease' ? 'order.rental.read' : 'order.service.read',
      keyword: salespersonSearch.keyword
    }),
    staleTime: 60_000
  })
  const subjects = useQuery({
    ...trpc.lookup.subjects.queryOptions({
      purpose: businessType === 'lease' ? 'order.rental.read' : 'order.service.read',
      keyword: subjectSearch.keyword
    }),
    staleTime: 60_000
  })
  const sites = useQuery({
    ...trpc.lookup.sites.queryOptions({
      purpose: businessType === 'lease' ? 'order.rental.read' : 'order.service.read',
      keyword: siteSearch.keyword
    }),
    enabled: includeSite,
    staleTime: 60_000
  })

  return {
    business: {
      loading: businesses.isFetching,
      onSearch: businessSearch.onSearch,
      options: mergeOptions(
        (businessSearch.keyword ? [] : rows).flatMap((row) =>
          row.businessId && row.businessName ? [{ label: row.businessName, value: row.businessId }] : []
        ),
        (businesses.data ?? []).map(({ label, value }) => ({ label, value }))
      )
    },
    customer: {
      loading: customers.isFetching,
      onSearch: customerSearch.onSearch,
      options: mergeOptions(
        (customerSearch.keyword ? [] : rows).map(({ customerId, customerName }) => ({
          label: customerName,
          value: customerId
        })),
        customers.data ?? []
      )
    },
    salesperson: {
      loading: salespeople.isFetching,
      onSearch: salespersonSearch.onSearch,
      options: mergeOptions(
        (salespersonSearch.keyword ? [] : rows).flatMap(({ salespersonId, salespersonName }) =>
          salespersonId === null ? [] : [{ label: salespersonName, value: salespersonId }]
        ),
        (salespeople.data ?? []).map(({ label, value }) => ({ label, value }))
      )
    },
    subject: {
      loading: subjects.isFetching,
      onSearch: subjectSearch.onSearch,
      options: mergeOptions(
        (subjectSearch.keyword ? [] : rows).flatMap((row) =>
          row.subjectId && row.subjectName ? [{ label: row.subjectName, value: row.subjectId }] : []
        ),
        (subjects.data?.data ?? []).map(({ name: label, id: value }) => ({ label, value }))
      )
    },
    site: includeSite
      ? {
          loading: sites.isFetching,
          onSearch: siteSearch.onSearch,
          options: mergeOptions(
            (siteSearch.keyword ? [] : rows).flatMap((row) =>
              row.siteId && row.siteName ? [{ label: row.siteName, value: row.siteId }] : []
            ),
            sites.data ?? []
          )
        }
      : undefined
  }
}
